import React, { useEffect, useMemo, useState } from 'react';
import { Trash2, AlertCircle, ArrowRight, TrendingDown, TrendingUp, Minus } from 'lucide-react';
import { Button } from '../../../../../components/ui/Button';
import { Card } from '../../../../../components/ui/Card';
import { ConfirmDialog } from '../../../../../components/shared/ConfirmDialog';
import { listSnapshots, loadSnapshot, deleteSnapshot, diffSnapshots, type SnapshotIndexEntry, type SnapshotDiff } from '../../../../../governance/pipeline/snapshots';
import { severityColor, severityLabel } from '../../../../../governance/utils/severity';
import { Badge } from '../../../../../components/ui/Badge';
interface CompareSnapshotsTabProps {
  refreshTrigger: number;
}
export function CompareSnapshotsTab({
  refreshTrigger
}: CompareSnapshotsTabProps) {
  const [snapshots, setSnapshots] = useState<SnapshotIndexEntry[]>([]);
  const [baseId, setBaseId] = useState<string>('');
  const [targetId, setTargetId] = useState<string>('');
  const [diff, setDiff] = useState<SnapshotDiff | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  useEffect(() => {
    const list = listSnapshots();
    setSnapshots(list);
    if (list.length >= 2 && !baseId && !targetId) {
      // Default to comparing the two most recent
      setTargetId(list[0].id);
      setBaseId(list[1].id);
    }
  }, [refreshTrigger]);
  const handleCompare = () => {
    setError(null);
    if (!baseId || !targetId) {
      setError('Selecteer twee snapshots om te vergelijken.');
      return;
    }
    if (baseId === targetId) {
      setError('Selecteer twee verschillende snapshots.');
      return;
    }
    const base = loadSnapshot(baseId);
    const target = loadSnapshot(targetId);
    if (!base || !target) {
      setError('Kon een of beide snapshots niet laden.');
      return;
    }
    // Ensure base is older than target for logical diffing
    const isBaseOlder = new Date(base.createdAt) < new Date(target.createdAt);
    const actualBase = isBaseOlder ? base : target;
    const actualTarget = isBaseOlder ? target : base;
    if (!isBaseOlder) {
      setBaseId(actualBase.id);
      setTargetId(actualTarget.id);
    }
    setDiff(diffSnapshots(actualBase, actualTarget));
  };
  const handleDelete = (id: string) => {
    setPendingDeleteId(id);
  };
  const confirmDelete = () => {
    const id = pendingDeleteId;
    if (!id) return;
    deleteSnapshot(id);
    setSnapshots(listSnapshots());
    if (baseId === id) setBaseId('');
    if (targetId === id) setTargetId('');
    if (diff?.baseId === id || diff?.targetId === id) setDiff(null);
    setPendingDeleteId(null);
  };
  const pendingSnapshot = pendingDeleteId ? snapshots.find(s => s.id === pendingDeleteId) : null;
  const formatDate = (iso: string) => {
    return new Date(iso).toLocaleString('nl-NL', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  return <div className="space-y-6" data-id="element-4754">
      <Card className="p-5" data-id="element-4755">
        <div className="flex flex-col md:flex-row gap-4 items-end" data-id="element-4756">
          <div className="flex-1 w-full" data-id="element-4757">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4758">
              Base Snapshot (Ouder)
            </label>
            <select value={baseId} onChange={e => setBaseId(e.target.value)} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500" data-id="element-4759">
              <option value="" data-id="element-4760">-- Selecteer --</option>
              {snapshots.map(s => <option key={s.id} value={s.id} data-id="element-4761">
                  {formatDate(s.createdAt)} {s.label ? `- ${s.label}` : ''} (
                  {s.totalIssues} issues)
                </option>)}
            </select>
          </div>
          <div className="flex-1 w-full" data-id="element-4762">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4763">
              Target Snapshot (Nieuwer)
            </label>
            <select value={targetId} onChange={e => setTargetId(e.target.value)} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500" data-id="element-4764">
              <option value="" data-id="element-4765">-- Selecteer --</option>
              {snapshots.map(s => <option key={s.id} value={s.id} data-id="element-4766">
                  {formatDate(s.createdAt)} {s.label ? `- ${s.label}` : ''} (
                  {s.totalIssues} issues)
                </option>)}
            </select>
          </div>
          <Button variant="primary" onClick={handleCompare} disabled={!baseId || !targetId} data-id="element-4767">
            Vergelijk
          </Button>
        </div>

        {error && <div className="mt-4 p-3 bg-red-50 text-red-700 rounded-md flex items-center gap-2 text-sm" data-id="element-4768">
            <AlertCircle className="w-4 h-4" data-id="element-4769" />
            {error}
          </div>}

        <div className="mt-4 pt-4 border-t border-gray-100" data-id="element-4770">
          <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-4771">
            Opgeslagen Snapshots ({snapshots.length}/20)
          </h4>
          <div className="flex flex-wrap gap-2" data-id="element-4772">
            {snapshots.map(s => <div key={s.id} className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-md px-3 py-1.5 text-sm" data-id="element-4773">
                <span className="text-gray-600" data-id="element-4774">{formatDate(s.createdAt)}</span>
                {s.label && <span className="font-medium text-gray-900" data-id="element-4775">{s.label}</span>}
                <button onClick={() => handleDelete(s.id)} className="text-gray-400 hover:text-red-600 transition-colors ml-1" title="Verwijderen" data-id="element-4776">
                  <Trash2 className="w-3.5 h-3.5" data-id="element-4777" />
                </button>
              </div>)}
            {snapshots.length === 0 && <span className="text-sm text-gray-500 italic" data-id="element-4778">
                Geen snapshots gevonden.
              </span>}
          </div>
        </div>
      </Card>

      {diff && <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-4779">
          {/* Aggregate Deltas */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-4780">
            <DeltaStat label="Totaal Issues" delta={diff.aggregate.totalIssues} data-id="element-4781" />
            <DeltaStat label="Blockers" delta={diff.aggregate.issuesByKind.blocker} data-id="element-4782" />
            <DeltaStat label="Coach" delta={diff.aggregate.issuesByKind.coach} data-id="element-4783" />
            <DeltaStat label="Info" delta={diff.aggregate.issuesByKind.info} data-id="element-4784" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4785">
            {/* Top Code Changes */}
            <Card className="p-5" data-id="element-4786">
              <h3 className="font-semibold text-gray-900 mb-4" data-id="element-4787">
                Code Verschuivingen
              </h3>
              <div className="space-y-2" data-id="element-4788">
                {diff.codeChanges.filter(c => c.delta !== 0).slice(0, 10).map(c => <div key={c.code} className="flex items-center justify-between p-2 rounded-md bg-gray-50 border border-gray-100" data-id="element-4789">
                      <span className="font-mono text-xs text-gray-700" data-id="element-4790">
                        {c.code}
                      </span>
                      <div className="flex items-center gap-3 text-sm" data-id="element-4791">
                        <span className="text-gray-500 w-8 text-right" data-id="element-4792">
                          {c.base}
                        </span>
                        <ArrowRight className="w-3 h-3 text-gray-400" data-id="element-4793" />
                        <span className="font-medium text-gray-900 w-8" data-id="element-4794">
                          {c.target}
                        </span>
                        <span className={`w-12 text-right font-medium ${c.delta > 0 ? 'text-red-600' : 'text-emerald-600'}`} data-id="element-4795">
                          {c.delta > 0 ? '+' : ''}
                          {c.delta}
                        </span>
                      </div>
                    </div>)}
                {diff.codeChanges.filter(c => c.delta !== 0).length === 0 && <p className="text-sm text-gray-500 italic" data-id="element-4796">
                    Geen wijzigingen in issue codes.
                  </p>}
              </div>
            </Card>

            {/* Verdict Flips */}
            <Card className="p-5" data-id="element-4797">
              <h3 className="font-semibold text-gray-900 mb-4" data-id="element-4798">
                Verdict Flips ({diff.verdictChanges.length})
              </h3>
              <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2" data-id="element-4799">
                {diff.verdictChanges.map(v => <div key={v.qid} className="flex items-center justify-between p-2 rounded-md bg-gray-50 border border-gray-100" data-id="element-4800">
                    <span className="text-sm font-medium text-gray-700" data-id="element-4801">
                      {v.qid}
                    </span>
                    <div className="flex items-center gap-2 text-xs" data-id="element-4802">
                      <VerdictBadge verdict={v.from} data-id="element-4803" />
                      <ArrowRight className="w-3 h-3 text-gray-400" data-id="element-4804" />
                      <VerdictBadge verdict={v.to} data-id="element-4805" />
                    </div>
                  </div>)}
                {diff.verdictChanges.length === 0 && <p className="text-sm text-gray-500 italic" data-id="element-4806">
                    Geen vragen zijn van verdict veranderd.
                  </p>}
              </div>
            </Card>
          </div>
        </div>}

      <ConfirmDialog open={pendingDeleteId !== null} title="Snapshot verwijderen?" message={pendingSnapshot ? `Snapshot van ${formatDate(pendingSnapshot.createdAt)}${pendingSnapshot.label ? ` ("${pendingSnapshot.label}")` : ''} wordt definitief verwijderd. Dit kan niet ongedaan gemaakt worden.` : 'Deze snapshot wordt definitief verwijderd. Dit kan niet ongedaan gemaakt worden.'} confirmLabel="Verwijderen" cancelLabel="Annuleren" variant="danger" onConfirm={confirmDelete} onCancel={() => setPendingDeleteId(null)} data-id="element-4807" />
    </div>;
}
function DeltaStat({
  label,
  delta
}: {
  label: string;
  delta: number;
}) {
  const isPositive = delta > 0;
  const isNegative = delta < 0;
  const isZero = delta === 0;
  return <Card className="p-4 flex flex-col" data-id="element-4808">
      <span className="text-sm text-gray-500 font-medium" data-id="element-4809">{label}</span>
      <div className="mt-2 flex items-center gap-2" data-id="element-4810">
        <span className={`text-2xl font-bold ${isZero ? 'text-gray-900' : isPositive ? 'text-red-600' : 'text-emerald-600'}`} data-id="element-4811">
          {isPositive ? '+' : ''}
          {delta}
        </span>
        {!isZero && (isPositive ? <TrendingUp className="w-5 h-5 text-red-500" data-id="element-4812" /> : <TrendingDown className="w-5 h-5 text-emerald-500" data-id="element-4813" />)}
        {isZero && <Minus className="w-5 h-5 text-gray-400" data-id="element-4814" />}
      </div>
    </Card>;
}
function VerdictBadge({
  verdict
}: {
  verdict: string;
}) {
  const colors = {
    PASS: 'bg-emerald-100 text-emerald-800',
    FAIL: 'bg-red-100 text-red-800',
    REVIEW_NEEDED: 'bg-amber-100 text-amber-800'
  };
  return <span className={`px-2 py-0.5 rounded-full font-medium ${colors[verdict as keyof typeof colors] || 'bg-gray-100 text-gray-800'}`} data-id="element-4815">
      {verdict}
    </span>;
}